import React, {useState, useEffect, useCallback, useRef} from "react"
import {Box, Typography, Chip, Button, Skeleton, Collapse, Link, CircularProgress} from "@mui/material"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import {format} from "date-fns"

import {DraftReplyPanel} from "./DraftReplyPanel"

import {api} from "@indebted/api"
import type {ConversationMessage, ConversationMessageAttachment, ConversationTimeline} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {colors} from "@indebted/theme/colors"

const PAGE_SIZE = 5
const REPLY_SYNC_ATTEMPTS = 8
const REPLY_SYNC_INTERVAL_MS = 700

function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

function formatOccurredAt(occurredAt: string): string {
	return format(new Date(occurredAt), "d MMM yyyy, HH:mm")
}

function directionChipProps(direction: string): {label: string; color: "secondary" | "primary" | "info"} {
	switch (direction) {
		case "inbound":
			return {label: "Customer", color: "secondary"}
		case "agent-reply":
			return {label: "Agent reply", color: "info"}
		default:
			return {label: "Auto-reply", color: "primary"}
	}
}

function deliveryChipProps(status: string): {label: string; color: "success" | "warning" | "error"} | null {
	switch (status) {
		case "delivered":
			return {label: "Delivered", color: "success"}
		case "temporarily-undelivered":
			return {label: "Delivery delayed", color: "warning"}
		case "permanently-undelivered":
			return {label: "Not delivered", color: "error"}
		default:
			return null
	}
}

// A reply row reuses the providerMessageId of the inbound message it answers, so
// the timeline can hold two rows sharing that id; identity has to include the
// direction (and occurredAt) to stay unique for React keys, refs, and highlight.
function rowKey(message: ConversationMessage): string {
	return `${message.direction}:${message.providerMessageId}:${message.occurredAt}`
}

function MessageRow({
	message,
	customerID,
	conversationID,
	isAnchor,
	isReplyTarget,
	targetIsEarlier,
	isHighlighted,
	replyToTarget,
	onJumpToTarget,
	onDraftReply,
	registerRef,
}: {
	message: ConversationMessage
	customerID: string
	conversationID: string
	isAnchor: boolean
	isReplyTarget: boolean
	targetIsEarlier: boolean
	isHighlighted: boolean
	replyToTarget?: {rowKey: string; occurredAt: string}
	onJumpToTarget?: (rowKey: string) => void
	onDraftReply?: () => void
	registerRef?: (element: HTMLElement | null) => void
}) {
	const [showQuoted, setShowQuoted] = useState(false)
	const {notification} = useNotification()
	const isInbound = message.direction === "inbound"
	const {label: directionLabel, color: directionColor} = directionChipProps(message.direction)
	const deliveryChip = message.deliveryStatus ? deliveryChipProps(message.deliveryStatus) : null

	const headerChip =
		isReplyTarget && targetIsEarlier ? (
			<Chip label="Replying to this" size="small" color="primary" />
		) : isAnchor ? (
			targetIsEarlier ? (
				<Chip label="Latest message" size="small" />
			) : (
				<Chip label="Reply anchor" size="small" color="primary" />
			)
		) : null

	const downloadAttachment = async (attachment: ConversationMessageAttachment) => {
		try {
			// API Gateway only base64-decodes the response when the request asks for a
			// binary media type, so a plain navigation (text/html Accept) would save the
			// raw base64 text instead of the file.
			const response = await fetch(
				api.customer.getConversationMessageAttachmentURL(
					customerID,
					conversationID,
					message.providerMessageId,
					attachment.index,
				),
				{headers: {Accept: "application/octet-stream"}},
			)
			if (!response.ok) {
				notification.error("Failed to download the attachment.")
				return
			}
			const objectURL = URL.createObjectURL(await response.blob())
			const link = document.createElement("a")
			link.href = objectURL
			link.download = attachment.filename
			document.body.appendChild(link)
			link.click()
			link.remove()
			URL.revokeObjectURL(objectURL)
		} catch {
			notification.error("Failed to download the attachment.")
		}
	}

	return (
		<Box
			component="li"
			ref={registerRef}
			p={3}
			border="1px solid"
			borderColor={isReplyTarget ? "primary.main" : "grey.200"}
			borderRadius={1}
			sx={{
				listStyle: "none",
				transition: "background-color 300ms ease",
				bgcolor: isHighlighted ? "action.selected" : "transparent",
			}}
		>
			<Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={0.5}>
				<Box display="flex" alignItems="center" gap={1} minWidth={0}>
					<Chip label={directionLabel} size="small" color={directionColor} />
					<Typography variant="subtitle2" color="textPrimary" noWrap>
						{message.subject}
					</Typography>
				</Box>
				{headerChip}
			</Box>
			<Box display="flex" alignItems="center" gap={1}>
				<Typography variant="caption" color="textSecondary">
					{formatOccurredAt(message.occurredAt)}
					{message.direction === "agent-reply" && message.authoredBy && ` · by ${message.authoredBy}`}
				</Typography>
				{isInbound && message.classificationCategory && (
					<Chip label={message.classificationCategory} size="small" variant="outlined" />
				)}
				{deliveryChip && (
					<>
						<Chip label={deliveryChip.label} size="small" color={deliveryChip.color} variant="outlined" />
						{message.deliveryStatus === "delivered" && message.deliveryOccurredAt && (
							<Typography variant="caption" color="textSecondary">
								at {formatOccurredAt(message.deliveryOccurredAt)}
							</Typography>
						)}
						{message.deliveryStatus !== "delivered" && message.deliveryReason && (
							<Typography variant="caption" color="textSecondary">
								· {message.deliveryReason}
							</Typography>
						)}
					</>
				)}
			</Box>
			{message.inReplyToProviderMessageId && (
				<Box mt={0.5}>
					{replyToTarget && onJumpToTarget ? (
						<Link
							component="button"
							type="button"
							variant="caption"
							underline="hover"
							onClick={() => onJumpToTarget(replyToTarget.rowKey)}
						>
							↳ In reply to the message from {formatOccurredAt(replyToTarget.occurredAt)}
						</Link>
					) : (
						<Typography variant="caption" color="textSecondary">
							↳ In reply to an earlier message
						</Typography>
					)}
				</Box>
			)}
			{message.body && (
				<Typography variant="body2" color="textPrimary" mt={1} sx={{whiteSpace: "pre-wrap"}}>
					{message.body}
				</Typography>
			)}
			{message.attachments && message.attachments.length > 0 && (
				<Box mt={1} display="flex" flexWrap="wrap" gap={1}>
					{message.attachments.map((attachment) => (
						<Chip
							key={attachment.index}
							label={attachment.filename}
							size="small"
							icon={<AttachFileIcon />}
							onClick={() => downloadAttachment(attachment)}
						/>
					))}
				</Box>
			)}
			{message.quoted && (
				<Box mt={1}>
					<Button
						variant="text"
						size="small"
						onClick={() => setShowQuoted((shown) => !shown)}
						sx={{alignSelf: "flex-start"}}
					>
						{showQuoted ? "Hide quoted text" : "Show quoted text"}
					</Button>
					<Collapse in={showQuoted} unmountOnExit>
						<Typography
							variant="body2"
							color="textSecondary"
							sx={{
								whiteSpace: "pre-wrap",
								borderLeft: `2px solid ${colors.gray["200"]}`,
								pl: 1.5,
								mt: 0.5,
							}}
						>
							{message.quoted}
						</Typography>
					</Collapse>
				</Box>
			)}
			{onDraftReply && (
				<Box mt={1}>
					<Button
						variant="outlined"
						size="small"
						onClick={onDraftReply}
						disabled={isReplyTarget}
						sx={{alignSelf: "flex-start"}}
					>
						{isReplyTarget ? "Drafting reply" : "Draft reply"}
					</Button>
				</Box>
			)}
		</Box>
	)
}

function ConversationDetail({customerID, conversationID}: {customerID: string; conversationID: string}) {
	const {notification} = useNotification()
	const [timeline, setTimeline] = useState<ConversationTimeline | null>(null)
	const [messages, setMessages] = useState<ConversationMessage[]>([])
	const [hasMore, setHasMore] = useState(false)
	const [loading, setLoading] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [selectedReplyMessageId, setSelectedReplyMessageId] = useState<string | null>(null)
	const [highlightedRowKey, setHighlightedRowKey] = useState<string | null>(null)
	const [syncingReply, setSyncingReply] = useState(false)
	const [syncTimedOut, setSyncTimedOut] = useState(false)

	const mounted = useRef(true)
	const messageRefs = useRef<Record<string, HTMLElement | null>>({})
	const highlightTimer = useRef<ReturnType<typeof setTimeout>>()
	const loadedCountRef = useRef(0)
	useEffect(() => {
		return () => {
			mounted.current = false
			clearTimeout(highlightTimer.current)
		}
	}, [])

	const jumpToMessage = useCallback((key: string) => {
		const element = messageRefs.current[key]
		if (!element) return
		element.scrollIntoView({behavior: "smooth", block: "center"})
		setHighlightedRowKey(key)
		clearTimeout(highlightTimer.current)
		highlightTimer.current = setTimeout(() => {
			if (mounted.current) setHighlightedRowKey(null)
		}, 1600)
	}, [])

	const fetchTimeline = useCallback(async () => {
		// Refetch covers every message already loaded, so it keeps the "Show earlier"
		// history instead of collapsing back to the first page.
		const conversation = await api.customer.getConversation(customerID, conversationID, {
			limit: Math.max(PAGE_SIZE, loadedCountRef.current),
			offset: 0,
		})
		if (!mounted.current) return null
		setTimeline(conversation)
		setMessages(conversation.messages)
		setHasMore(conversation.hasMore)
		loadedCountRef.current = conversation.messages.length
		return conversation
	}, [customerID, conversationID])

	const reloadConversation = useCallback(async () => {
		try {
			await fetchTimeline()
		} catch {
			if (mounted.current) notification.error("Failed to load the conversation")
		}
	}, [fetchTimeline, notification])

	const waitForSentReply = useCallback(
		async (repliedToProviderMessageId: string) => {
			// The reply is recorded synchronously but its timeline row is projected
			// asynchronously, so poll the refetch until the reply lands rather than
			// settling on a timeline that is briefly missing the reply just sent.
			setSyncingReply(true)
			setSyncTimedOut(false)
			try {
				for (let attempt = 0; attempt < REPLY_SYNC_ATTEMPTS; attempt++) {
					try {
						const conversation = await fetchTimeline()
						if (!conversation) return
						const landed = conversation.messages.some(
							(m) =>
								m.direction === "agent-reply" &&
								m.inReplyToProviderMessageId === repliedToProviderMessageId,
						)
						if (landed) return
					} catch {
						// A transient read failure should not abort the sync; keep polling.
					}
					if (attempt < REPLY_SYNC_ATTEMPTS - 1) await delay(REPLY_SYNC_INTERVAL_MS)
				}
				// Polling settled without the reply: the send succeeded but the projection
				// is still catching up, so flag that the timeline may not show it yet.
				if (mounted.current) setSyncTimedOut(true)
			} finally {
				if (mounted.current) setSyncingReply(false)
			}
		},
		[fetchTimeline],
	)

	useEffect(() => {
		setLoading(true)
		reloadConversation().finally(() => {
			if (mounted.current) setLoading(false)
		})
	}, [reloadConversation])

	function showEarlier() {
		setLoadingMore(true)
		api.customer
			.getConversation(customerID, conversationID, {limit: PAGE_SIZE, offset: messages.length})
			.then((conversation) => {
				setMessages((current) => [...current, ...conversation.messages])
				setHasMore(conversation.hasMore)
				loadedCountRef.current += conversation.messages.length
			})
			.catch(() => {
				notification.error("Failed to load earlier messages")
			})
			.finally(() => {
				setLoadingMore(false)
			})
	}

	if (loading) {
		return (
			<Box display="flex" flexDirection="column" gap={2.5}>
				{[0, 1].map((i) => (
					<Box key={i} p={4} border="1px solid" borderColor="grey.200" borderRadius={1}>
						<Box display="flex" justifyContent="space-between" mb={1.5}>
							<Skeleton variant="text" width={160} />
							<Skeleton variant="rounded" width={72} height={20} />
						</Box>
						<Skeleton variant="text" width="90%" />
						<Skeleton variant="text" width="75%" />
					</Box>
				))}
			</Box>
		)
	}

	if (!timeline) {
		return null
	}

	const replyTarget = selectedReplyMessageId ?? timeline.replyAnchorProviderMessageId
	const targetIsEarlier = Boolean(replyTarget) && replyTarget !== timeline.replyAnchorProviderMessageId
	const inboundById = new Map(messages.filter((m) => m.direction === "inbound").map((m) => [m.providerMessageId, m]))

	return (
		<Box
			sx={{borderTop: `1px solid ${colors.gray["200"]}`, pt: 2.5}}
			display="flex"
			gap={3}
			alignItems="flex-start"
		>
			<Box flex={1} display="flex" flexDirection="column" gap={1.5} minWidth={0}>
				{syncingReply && (
					<Box display="flex" alignItems="center" gap={1}>
						<CircularProgress size={14} />
						<Typography variant="caption" color="textSecondary">
							Updating timeline…
						</Typography>
					</Box>
				)}
				{syncTimedOut && !syncingReply && (
					<Typography variant="caption" color="textSecondary">
						Reply sent. It is taking longer than usual to appear — refresh to confirm.
					</Typography>
				)}
				<Box component="ul" display="flex" flexDirection="column" gap={1.5} m={0} p={0}>
					{messages.map((m) => {
						const answeredMessage = m.inReplyToProviderMessageId
							? inboundById.get(m.inReplyToProviderMessageId)
							: undefined
						const key = rowKey(m)
						const isInbound = m.direction === "inbound"
						return (
							<MessageRow
								key={key}
								message={m}
								customerID={customerID}
								conversationID={conversationID}
								isAnchor={isInbound && m.providerMessageId === timeline.replyAnchorProviderMessageId}
								isReplyTarget={isInbound && m.providerMessageId === replyTarget}
								targetIsEarlier={targetIsEarlier}
								isHighlighted={highlightedRowKey === key}
								replyToTarget={
									answeredMessage
										? {rowKey: rowKey(answeredMessage), occurredAt: answeredMessage.occurredAt}
										: undefined
								}
								onJumpToTarget={jumpToMessage}
								onDraftReply={
									isInbound ? () => setSelectedReplyMessageId(m.providerMessageId) : undefined
								}
								registerRef={(element) => {
									messageRefs.current[key] = element
								}}
							/>
						)
					})}
					{hasMore && (
						<Button
							variant="text"
							size="small"
							onClick={showEarlier}
							disabled={loadingMore}
							sx={{alignSelf: "flex-start"}}
						>
							{loadingMore ? "Loading…" : "Show earlier"}
						</Button>
					)}
				</Box>
			</Box>
			{replyTarget && (
				<Box flex={1}>
					<DraftReplyPanel
						key={replyTarget}
						customerID={customerID}
						conversationID={conversationID}
						providerMessageId={replyTarget}
						repliesToEarlierMessage={targetIsEarlier}
						onSent={() => void waitForSentReply(replyTarget)}
					/>
				</Box>
			)}
		</Box>
	)
}

export {ConversationDetail}
